import { supabase } from "@/integrations/supabase/client";
import { safeApiCall } from "./index";
import { logActivity, ActivityActions } from "@/utils/activityLogger";
import { v4 as uuidv4 } from 'uuid';
import PurchaseService from "@/services/PurchaseService";
import { isExpenseCostRecognized } from "@/lib/paymentMethods";

// Define placeholder types for missing tables to reduce 'any' usage
// IMPORTANT: Regenerate Supabase types for proper definitions!
type ProjectShare = {
  id: string;
  share_code: string;
  project_id: string;
  from_user_id: string;
  to_user_id?: string | null;
  mode: 'read' | 'write';
  status: 'pending' | 'active' | 'revoked' | 'expired';
  expires_at?: string | null;
  created_at: string;
  used_at?: string | null;
  revoked_at?: string | null;
  project?: any; // Placeholder for joined project data
  to_user?: any; // Placeholder for joined user data
};

type ProjectShareLog = {
  id?: string;
  share_id: string;
  action: string;
  performed_by: string;
  note?: string;
  created_at?: string;
};

// Define project types
interface ProjectDetails {
  parcel?: string;
  lot?: string;
  flats?: number;
  shops?: number;
  area?: number;
  units: any[]; // You might want to define a proper type for units
}

interface Project {
  id: string;
  name: string;
  user_id: string;
  created_at: string;
  parcel?: string;
  lot?: string;
  flats?: number;
  shops?: number;
  area?: number;
  details?: ProjectDetails;
  // Add other project fields as needed
}

// Projects API
export const projectsAPI = {
  // Get all projects
  async getProjects() {
    
    
    try {
      return await safeApiCall(
        async () => {
          
          
          // Get user session to get current user ID
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) throw new Error('User not authenticated');
          
          const currentUserId = session.user.id;
          
          // Get user's projects using the project_user_link table
          const { data, error } = await supabase
            .from('project_user_link')
            .select('project:project_id(*)')
            .eq('user_id', currentUserId);
          
          if (error) {
            
            
            throw error;
          }
          
          // Transform data to extract project data from nested structure
          const projects = data?.map(item => {
            // First cast to unknown, then to Project to avoid direct any[] to Project conversion
            const project = (item.project as unknown) as Project;
            
            // Add backward compatibility for details
            if (project) {
              const existingDetails = (project as any).details || {};
              project.details = {
                parcel: existingDetails.parcel ?? project.parcel ?? "",
                lot: existingDetails.lot ?? project.lot ?? "",
                flats: existingDetails.flats ?? project.flats ?? 0,
                shops: existingDetails.shops ?? project.shops ?? 0,
                area: existingDetails.area ?? project.area ?? 0,
                units: Array.isArray(existingDetails.units) ? existingDetails.units : []
              };
            }
            
            return project;
          }) || [];
          
          
          if (projects.length === 0) {
            
          }
          
          // Sort by created_at in descending order
          projects.sort((a: Project, b: Project) => {
            if (!a.created_at || !b.created_at) return 0;
            return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
          });
          
          return projects;
        },
        {
          timeoutMs: 10000,
          name: "getProjects",
          queueIfResuming: true,
          fallbackValue: [] // Return empty array if call is made during resume
        }
      );
    } catch (error) {
      
      if (error instanceof Error) {
        
        
      }
      // Return empty array as fallback in case of error
      return [];
    }
  },

  // Get a specific project by ID
  async getProject(projectId: string) {
    return safeApiCall(
      async () => {
        const { data, error } = await supabase
          .from('projects')
          .select('*')
          .eq('id', projectId)
          .single();

        if (error) {
          
          throw error;
        }

        // IMPORTANT: Regenerate Supabase types if 'details' exists
        // Use 'as any' as a temporary workaround for potential type mismatch
        const projectData = data as any;

        // Extract units from details.units if they exist
        if (projectData?.details?.units) {
           projectData.units = projectData.details.units;
        }

        return projectData;
      },
      {
        timeoutMs: 10000,
        name: `getProject_${projectId}`,
        queueIfResuming: true
      }
    );
  },

  // Create a new project
  async createProject(projectData: {
    name: string;
    description?: string;
    location?: string;
    start_date: Date;
    end_date?: Date;
    budget?: number;
    client_name?: string;
    client_email?: string;
    client_phone?: string;
    status: "active" | "completed" | "on-track" | "delayed";
    created_by: string;
    units?: Array<{type: 'flat' | 'shop', number: number, area: number}>;
    flats?: number;
    shops?: number;
    area?: number;
  }) {
    return safeApiCall(
      async () => {
        // Get current user info
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');
        
        // Check subscription status and project limit
        // Get current ACTIVE projects count only
        const { data: projectLinks } = await supabase
          .from('project_user_link')
          .select('project:project_id(id, status)')
          .eq('user_id', user.id);
        
        // Count only active projects (exclude completed/passive ones)
        const currentProjectCount = projectLinks?.filter((link: any) => 
          link.project && link.project.status !== 'completed'
        ).length || 0;
        
        // Get user's subscription info from subscription_status_view.
        // The view returns has_access=TRUE unconditionally for free tier and
        // a tier-aware active_project_limit (1 for free, 5 for premium).
        const { data: subscriptionData, error: subscriptionError } = await supabase
          .from('subscription_status_view')
          .select('tier, plan, active_project_limit, has_access')
          .eq('user_id', user.id)
          .single();

        if (subscriptionError) {
          const error = new Error('SUBSCRIPTION_INFO_ERROR');
          error.name = 'SUBSCRIPTION_INFO_ERROR';
          throw error;
        }

        // NO_ACCESS only fires when a premium subscription has expired past
        // grace. Free users always have access.
        if (subscriptionData.has_access === false) {
          const error = new Error('NO_ACCESS');
          error.name = 'NO_ACCESS';
          throw error;
        }

        // Single project-limit error. UI branches messaging by tier.
        if (currentProjectCount >= (subscriptionData.active_project_limit ?? 1)) {
          const error = new Error('PROJECT_LIMIT_REACHED');
          error.name = 'PROJECT_LIMIT_REACHED';
          throw error;
        }
        
        // Prepare details JSON with unit information
        const details = {
          parcel: projectData.location, // Store location as parcel
          lot: projectData.location?.split('/')[1] || '', // Try to extract lot from location if it's in "parcel/lot" format
          flats: projectData.flats || 0,
          shops: projectData.shops || 0,
          area: projectData.area || 0,
          units: projectData.units || []
        };
        
        // Map input data to match Supabase schema
        const formattedData = {
          name: projectData.name,
          status: projectData.status,
          cost_try: projectData.budget, // Map budget to cost_try (note: renamed from cost_tl)
          user_id: user.id, // Use current user's ID instead of passed in created_by
          details: details // Store additional details as JSON
        };
        
        const { data, error } = await supabase
          .from('projects')
          .insert(formattedData)
          .select()
          .single();
        
        if (error) {
          throw error;
        }
        
        // Also create a project_user_link entry with owner role
        const { error: linkError } = await supabase
          .from('project_user_link')
          .insert({
            project_id: data.id,
            user_id: user.id,
            role: 'owner',
            linked_via: 'self'
          });
          
        if (linkError) {
          
        }
        
        // Log the project creation activity
        await logActivity(
          user.id,
          ActivityActions.CREATE_PROJECT,
          {
            project_id: data.id,
            project_name: data.name,
            status: data.status
          }
        );
        
        return data;
      },
      {
        timeoutMs: 15000,
        name: "createProject",
        critical: true // Creating a project is a critical operation
      }
    );
  },

  // Update a project
  async updateProject(
    projectId: string,
    projectData: {
      name?: string;
      description?: string;
      location?: string;
      start_date?: Date;
      end_date?: Date;
      budget?: number;
      client_name?: string;
      client_email?: string;
      client_phone?: string;
      status?: "active" | "completed" | "on-track" | "delayed";
      progress?: number;
      details?: any;
    }
  ) {
    return safeApiCall(
      async () => {
        // Get current user to ensure we're only updating our own projects
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        // Map input data to match Supabase schema
        const formattedData: any = {
          name: projectData.name,
          status: projectData.status,
          cost_try: projectData.budget, // Map budget to cost_try
          progress: projectData.progress, // Include progress
          description: projectData.description // Make sure description is included
        };
        
        // Handle details object if provided
        if (projectData.details) {
          formattedData.details = projectData.details;
        }
        
        // Remove undefined fields to avoid overwriting with null
        Object.keys(formattedData).forEach(key => {
          if (formattedData[key] === undefined) {
            delete formattedData[key];
          }
        });
        
        
        
        
        try {
          // RLS will ensure we can only update our own projects
          const { data, error } = await supabase
            .from('projects')
            .update(formattedData)
            .eq('id', projectId)
            .eq('user_id', user.id) // Extra safety check to ensure we only update our own projects
            .select(); // Remove the .single() to avoid the PGRST116 error
          
          if (error) {
            
            
            throw error;
          }
          
          if (!data || data.length === 0) {
            
            throw new Error(`Project with ID ${projectId} not found or not owned by you`);
          }
          
          // Log the project update activity
          await logActivity(
            user.id,
            ActivityActions.UPDATE_PROJECT,
            {
              project_id: projectId,
              project_name: formattedData.name,
              changes: Object.keys(formattedData).join(', ')
            }
          );
          
          return data[0]; // Return the first result
        } catch (error) {
          
          throw error;
        }
      },
      {
        timeoutMs: 15000,
        name: `updateProject_${projectId}`,
        critical: true // Updating a project is a critical operation
      }
    );
  },

  // Delete a project
  async deleteProject(projectId: string) {
    return safeApiCall(
      async () => {
        // Get current user to ensure we're only deleting our own projects
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        // Get project name before deletion for activity log
        const { data: projectData } = await supabase
          .from('projects')
          .select('name')
          .eq('id', projectId)
          .eq('user_id', user.id)
          .single();

        // RLS will ensure we can only delete our own projects,
        // but we add an extra safety check
        const { error } = await supabase
          .from('projects')
          .delete()
          .eq('id', projectId)
          .eq('user_id', user.id); // Extra safety check
        
        if (error) {
          
          throw error;
        }
        
        // Log the project deletion activity
        await logActivity(
          user.id,
          ActivityActions.DELETE_PROJECT,
          {
            project_id: projectId,
            project_name: projectData?.name || 'Unknown'
          }
        );
        
        return true;
      },
      {
        timeoutMs: 15000,
        name: `deleteProject_${projectId}`,
        critical: true // Deleting a project is a critical operation
      }
    );
  },

  // Complete a project (set status to completed)
  async completeProject(projectId: string) {
    return safeApiCall(
      async () => {
        
        
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');
        
        try {
          const { data, error } = await supabase
            .from('projects')
            .update({ status: 'completed' })
            .eq('id', projectId)
            .select('name')
            .single();
          
          if (error) {
            
            throw error;
          }
          
          // Log project completion
          await logActivity(
            user.id,
            ActivityActions.UPDATE_PROJECT,
            {
              project_id: projectId,
              project_name: data.name,
              status: 'completed',
              action: 'project_completion'
            }
          );
          
          return data;
        } catch (error) {
          
          throw error;
        }
      },
      {
        timeoutMs: 15000,
        name: `completeProject_${projectId}`,
        critical: true
      }
    );
  },

  // Get materials for a specific project
  async getProjectMaterials(projectId: string) {
    return safeApiCall(
      async () => {
        const { data, error } = await supabase
          .from('materials')
          .select('*')
          .eq('project_id', projectId)
          .order('created_at', { ascending: false });
        
        if (error) {
          
          throw error;
        }
        
        return data || [];
      },
      {
        timeoutMs: 10000,
        name: `getProjectMaterials_${projectId}`,
        fallbackValue: [] // Return empty array if call is made during resume
      }
    );
  },

  // Get site photos for a specific project
  async getProjectSitePhotos(projectId: string) {
    return safeApiCall(
      async () => {
        const { data, error } = await supabase
          .from('entries')
          .select('*')
          .eq('project_id', projectId)
          .eq('type', 'sitePhoto')
          .order('date', { ascending: false });
        
        if (error) {
          
          throw error;
        }
        
        return data || [];
      },
      {
        timeoutMs: 10000,
        name: `getProjectSitePhotos_${projectId}`,
        fallbackValue: [] // Return empty array if call is made during resume
      }
    );
  },

  // Upload a site photo for a project
  async uploadSitePhoto(file: File, projectId: string) {
    return safeApiCall(
      async () => {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
        const filePath = `site-photos/${projectId}/${fileName}`;
        
        // Upload the image
        const { error: uploadError } = await supabase.storage
          .from('project-files')
          .upload(filePath, file);
        
        if (uploadError) {
          
          throw uploadError;
        }
        
        // Get the public URL for the image
        const { data } = await supabase.storage
          .from('project-files')
          .getPublicUrl(filePath);
        
        if (!data || !data.publicUrl) {
          throw new Error('Failed to get public URL for site photo');
        }
        
        return data.publicUrl;
      },
      {
        timeoutMs: 30000, // File uploads can take longer
        name: "uploadSitePhoto",
        critical: true // User-initiated file uploads should be treated as critical
      }
    );
  },

  // Get site photo URL
  async getSitePhotoUrl(path: string) {
    return safeApiCall(
      async () => {
        const { data } = await supabase.storage
          .from('project-files')
          .getPublicUrl(path);
        
        if (!data || !data.publicUrl) {
          throw new Error('Failed to get public URL for site photo');
        }
        
        return data.publicUrl;
      },
      {
        timeoutMs: 5000,
        name: "getSitePhotoUrl"
      }
    );
  },

  // Increment the available projects for a user (for subscription management)
  async incrementAvailableProjects(userId: string) {
    return safeApiCall(
      async () => {
        // Fetch current user information
        const { data: userData, error: fetchError } = await supabase
          .from('users')
          .select('plan, projects_locked')
          .eq('id', userId)
          .single();

        if (fetchError) {
          
          throw fetchError;
        }

        // Update user to unlock projects if they were locked
        const { data, error } = await supabase
          .from('users')
          .update({
            projects_locked: false
          })
          .eq('id', userId)
          .select();

        if (error) {
          
          throw error;
        }

        return data;
      },
      {
        timeoutMs: 10000,
        name: "incrementAvailableProjects",
        critical: true // Subscription operations are critical
      }
    );
  },

  // Get upcoming check payments
  async getUpcomingCheckPayments() {
    
    
    return safeApiCall(
      async () => {
        // Create today at midnight for date comparison without time
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayStr = today.toISOString().split('T')[0];
        
        // Create date for 7 days from now
        const sevenDaysFromNow = new Date(today);
        sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
        sevenDaysFromNow.setHours(23, 59, 59, 999);
        const sevenDaysFromNowStr = sevenDaysFromNow.toISOString().split('T')[0];

        

        // Fetch all check entries with due dates in the future or today, up to 7 days
        try {
          
          
          const { data, error } = await supabase
            .from('entries')
            .select('*, projects(name)')
            .in('type', ['givenCheck', 'receivedCheck'])
            .is('is_processed', null) // Only get unprocessed checks
            .or('is_processed.eq.false')
            .gte('due_date', todayStr)
            .lte('due_date', sevenDaysFromNowStr) // Only fetch checks within 7 days
            .order('due_date', { ascending: true });

          if (error) {
            
            
            throw error;
          }

          

          // Process the data to calculate days until due
          const upcomingChecks = data.map(entry => {
            // Create a date object for the due date and reset time to midnight
            const dueDate = new Date(entry.due_date);
            dueDate.setHours(0, 0, 0, 0);
            
            // Calculate days between dates (reset both to midnight for accurate day calculation)
            const diffTime = dueDate.getTime() - today.getTime();
            const dueInDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
            
            // Only include checks from today and future (dueInDays >= 0)
            if (dueInDays < 0) return null;

            
            
            try {
              // Use original_amount and original_currency from the new database schema
              // With fallback to old fields for backward compatibility
              return {
                id: entry.id,
                projectId: entry.project_id,
                projectName: entry.projects?.name || 'Unknown Project',
                amount: (entry as any).original_amount !== undefined ? (entry as any).original_amount : entry.amount,
                dueDate: entry.due_date,
                dueInDays: dueInDays,
                isGivenCheck: entry.type === 'givenCheck',
                description: entry.description,
                currency: (entry as any).original_currency !== undefined ? (entry as any).original_currency : (entry.currency || 'TRY')
              };
            } catch (mapError) {
              
              
              return null;
            }
          }).filter(Boolean); // Remove null entries

          // Sort by due date (closest first)
          return upcomingChecks.sort((a, b) => a.dueInDays - b.dueInDays);
        } catch (err) {
          
          if (err instanceof Error) {
            
            
          }
          return [];
        }
      },
      {
        timeoutMs: 10000,
        name: "getUpcomingCheckPayments",
        fallbackValue: [] // Return empty array if call is made during resume
      }
    );
  },

  // Get upcoming check payments for a specific project
  async getProjectUpcomingCheckPayments(projectId: string) {
    
    
    return safeApiCall(
      async () => {
        // Use the database function we created to get upcoming check payments
        try {
          
          
          // Use 'any' type for now to avoid TypeScript errors with RPC functions
          const { data, error } = await (supabase as any)
            .rpc('get_project_upcoming_check_payments', {
              p_project_id: projectId
            });

          if (error) {
            
            
            throw error;
          }

          
          
          // Process the data returned from the function
          const upcomingChecks = data.map((entry: any) => {
            
            return {
              id: entry.id,
              projectId: entry.project_id,
              projectName: entry.project_name || 'Unknown Project',
              amount: entry.amount,
              dueDate: entry.due_date,
              dueInDays: entry.due_in_days,
              isGivenCheck: entry.is_given_check,
              description: entry.description,
              currency: entry.currency || 'TRY',
              userName: entry.user_name
            };
          });

          return upcomingChecks;
        } catch (err) {
          
          if (err instanceof Error) {
            
            
          }
          return [];
        }
      },
      {
        timeoutMs: 10000,
        name: `getProjectUpcomingCheckPayments_${projectId}`,
        fallbackValue: [] // Return empty array if call is made during resume
      }
    );
  },

  // Calculate official and unofficial costs for a project
  async getProjectCosts(projectId: string) {
    

    return safeApiCall(
      async () => {
        try {
          // First try to get the project data with detailed costs
          
          const { data: project, error: projectError } = await supabase
            .from('projects')
            // IMPORTANT: Select specific columns if 'detailed_costs' exists in your schema
            .select('cost_try, cost_usd, cost_eur') // Assuming detailed_costs might not exist or is complex
            .eq('id', projectId)
            .single();

          // Default structure for costs
          const defaultCosts = {
            official: { TRY: 0, USD: 0, EUR: 0 },
            unofficial: { TRY: 0, USD: 0, EUR: 0 },
            total: { TRY: 0, USD: 0, EUR: 0 }
          };

          if (projectError) {
            
            return defaultCosts;
          }

          // Using basic cost fields as primary source due to type/schema uncertainty
          
          const projectData = project as any; // Use 'as any' due to uncertainty

          const tryAmount = typeof projectData?.cost_try === 'number' ? projectData.cost_try : 0;
          const usdAmount = typeof projectData?.cost_usd === 'number' ? projectData.cost_usd : 0;
          const eurAmount = typeof projectData?.cost_eur === 'number' ? projectData.cost_eur : 0;

          // Assuming basic costs are official for now, adjust if needed
          return {
            official: {
              TRY: tryAmount,
              USD: usdAmount,
              EUR: eurAmount
            },
            unofficial: { // Assume 0 unofficial if not explicitly tracked
              TRY: 0,
              USD: 0,
              EUR: 0
            },
            total: {
              TRY: tryAmount,
              USD: usdAmount,
              EUR: eurAmount
            }
          };
        } catch (error) {
          
          // Return a valid structure with zeros
          return {
            official: { TRY: 0, USD: 0, EUR: 0 },
            unofficial: { TRY: 0, USD: 0, EUR: 0 },
            total: { TRY: 0, USD: 0, EUR: 0 }
          };
        }
      },
      {
        timeoutMs: 10000,
        name: `getProjectCosts_${projectId}`,
        fallbackValue: { // Ensure fallback is a valid object structure
          official: { TRY: 0, USD: 0, EUR: 0 },
          unofficial: { TRY: 0, USD: 0, EUR: 0 },
          total: { TRY: 0, USD: 0, EUR: 0 }
        }
      }
    );
  },

  // Get project costs directly from entries table
  async getProjectCostsFromEntries(projectId: string) {
    return safeApiCall(
      async () => {
        try {
          const { data: entries, error } = await supabase
            .from('entries')
            .select('*') // Select all, will use 'as any' below
            .eq('project_id', projectId)
            .eq('type', 'expense')
            .order('date', { ascending: false });

          if (error) {
            
            throw error;
          }

          // Initialize cost totals
          const costs = {
            TRY: { official: 0, unofficial: 0, total: 0 },
            USD: { official: 0, unofficial: 0, total: 0 },
            EUR: { official: 0, unofficial: 0, total: 0 },
            GBP: { official: 0, unofficial: 0, total: 0 },
            JPY: { official: 0, unofficial: 0, total: 0 },
            RUB: { official: 0, unofficial: 0, total: 0 },
            KWD: { official: 0, unofficial: 0, total: 0 },
            SAR: { official: 0, unofficial: 0, total: 0 },
            CHF: { official: 0, unofficial: 0, total: 0 },
            XAU: { official: 0, unofficial: 0, total: 0 },
            BTC: { official: 0, unofficial: 0, total: 0 }
          };

          // Process each entry
          entries.forEach(entryData => {
            // IMPORTANT: Using 'as any' due to potential type mismatches. Regenerate types.
            const entry = entryData as any;

            // Payment-method aware cost recognition: pending checks, unsettled
            // open accounts, and advance draw-downs do not count toward cost yet.
            if (!isExpenseCostRecognized(entry)) {
              return;
            }

            const keys = Object.keys(entry);

            Object.keys(costs).forEach(currency => {
              const lcCurrency = currency.toLowerCase();
              let addedAmount = 0;

              // Check new official/unofficial specific fields first
              const officialKey = `amount_${lcCurrency}_official`;
              if (keys.includes(officialKey) && entry[officialKey] != null) {
                const amount = parseFloat(entry[officialKey]);
                costs[currency].official += amount;
                addedAmount += amount;
              }
              const unofficialKey = `amount_${lcCurrency}_unofficial`;
              if (keys.includes(unofficialKey) && entry[unofficialKey] != null) {
                 const amount = parseFloat(entry[unofficialKey]);
                 costs[currency].unofficial += amount;
                 addedAmount += amount;
              }

              // Fallback to legacy fields if no new fields were found for this currency
              if (addedAmount === 0) {
                 const legacyKey = `amount_${lcCurrency}`;
                 if (keys.includes(legacyKey) && entry[legacyKey] != null) {
                   const amount = parseFloat(entry[legacyKey]);
                   // Use is_official field if available, otherwise assume official? (Needs clarification)
                   const isOfficial = entry.is_official === true;
                   if (isOfficial) {
                      costs[currency].official += amount;
                   } else {
                      costs[currency].unofficial += amount;
                   }
                   addedAmount += amount;
                 }
                 // Fallback to generic amount/currency if still nothing found
                 else if (addedAmount === 0 && entry.currency === currency && entry.amount != null) {
                     const amount = parseFloat(entry.amount);
                     const isOfficial = entry.is_official === true;
                     if (isOfficial) {
                         costs[currency].official += amount;
                     } else {
                         costs[currency].unofficial += amount;
                     }
                     addedAmount += amount;
                 }
              }

              costs[currency].total += addedAmount;
            });
          });

          // Return the aggregated costs
          return {
            officialCosts: costs.TRY.official,
            unofficialCosts: costs.TRY.unofficial,
            totalCosts_try: costs.TRY.total,
            officialCosts_usd: costs.USD.official,
            unofficialCosts_usd: costs.USD.unofficial,
            totalCosts_usd: costs.USD.total,
            officialCosts_eur: costs.EUR.official,
            unofficialCosts_eur: costs.EUR.unofficial,
            totalCosts_eur: costs.EUR.total,
            officialCosts_gbp: costs.GBP.official,
            unofficialCosts_gbp: costs.GBP.unofficial,
            totalCosts_gbp: costs.GBP.total,
            officialCosts_jpy: costs.JPY.official,
            unofficialCosts_jpy: costs.JPY.unofficial,
            totalCosts_jpy: costs.JPY.total,
            officialCosts_rub: costs.RUB.official,
            unofficialCosts_rub: costs.RUB.unofficial,
            totalCosts_rub: costs.RUB.total,
            officialCosts_kwd: costs.KWD.official,
            unofficialCosts_kwd: costs.KWD.unofficial,
            totalCosts_kwd: costs.KWD.total,
            officialCosts_sar: costs.SAR.official,
            unofficialCosts_sar: costs.SAR.unofficial,
            totalCosts_sar: costs.SAR.total,
            officialCosts_chf: costs.CHF.official,
            unofficialCosts_chf: costs.CHF.unofficial,
            totalCosts_chf: costs.CHF.total,
            officialCosts_xau: costs.XAU.official,
            unofficialCosts_xau: costs.XAU.unofficial,
            totalCosts_xau: costs.XAU.total,
            officialCosts_btc: costs.BTC.official,
            unofficialCosts_btc: costs.BTC.unofficial,
            totalCosts_btc: costs.BTC.total,
            detailedCosts: costs // Return the full structure
          };
        } catch (error) {
          
          throw error;
        }
      },
      {
        timeoutMs: 10000,
        name: `getProjectCostsFromEntries_${projectId}`,
        fallbackValue: { // Ensure fallback matches the return structure EXACTLY
          officialCosts: 0, unofficialCosts: 0, totalCosts_try: 0,
          officialCosts_usd: 0, unofficialCosts_usd: 0, totalCosts_usd: 0,
          officialCosts_eur: 0, unofficialCosts_eur: 0, totalCosts_eur: 0,
          officialCosts_gbp: 0, unofficialCosts_gbp: 0, totalCosts_gbp: 0,
          officialCosts_jpy: 0, unofficialCosts_jpy: 0, totalCosts_jpy: 0,
          officialCosts_rub: 0, unofficialCosts_rub: 0, totalCosts_rub: 0,
          officialCosts_kwd: 0, unofficialCosts_kwd: 0, totalCosts_kwd: 0,
          officialCosts_sar: 0, unofficialCosts_sar: 0, totalCosts_sar: 0,
          officialCosts_chf: 0, unofficialCosts_chf: 0, totalCosts_chf: 0,
          officialCosts_xau: 0, unofficialCosts_xau: 0, totalCosts_xau: 0,
          officialCosts_btc: 0, unofficialCosts_btc: 0, totalCosts_btc: 0,
          detailedCosts: { // Provide the full empty structure
            TRY: { official: 0, unofficial: 0, total: 0 }, USD: { official: 0, unofficial: 0, total: 0 },
            EUR: { official: 0, unofficial: 0, total: 0 }, GBP: { official: 0, unofficial: 0, total: 0 },
            JPY: { official: 0, unofficial: 0, total: 0 }, RUB: { official: 0, unofficial: 0, total: 0 },
            KWD: { official: 0, unofficial: 0, total: 0 }, SAR: { official: 0, unofficial: 0, total: 0 },
            CHF: { official: 0, unofficial: 0, total: 0 }, XAU: { official: 0, unofficial: 0, total: 0 },
            BTC: { official: 0, unofficial: 0, total: 0 }
          }
        }
      }
    );
  },

  // Get all check payments for a specific project
  async getAllProjectCheckPayments(projectId: string) {
    return safeApiCall(
      async () => {
        try {
          // Include legacy check entry-types AND consolidated expense-based
          // checks (payment_method='check', advance-by-check, and open accounts
          // settled by check) so they all flow through the same mark-paid UI.
          const { data, error } = await supabase
            .from('entries')
            .select('*, people(name)') // Fetch related person name
            .eq('project_id', projectId)
            .or(
              'type.in.(givenCheck,receivedCheck),' +
              'payment_method.eq.check,' +
              'and(payment_method.eq.advance,advance_sub_method.eq.check),' +
              'and(payment_method.eq.open_account,settled_method.eq.check)'
            )
            .order('date', { ascending: false });

          if (error) {
            
            throw error;
          }

          // Process the data
          const checkPayments = data.map(entryData => {
            // IMPORTANT: Using 'as any'. Regenerate Supabase types.
            const entry = entryData as any;

            const dueDate = entry.due_date ? new Date(entry.due_date) : null;
            const today = new Date(); today.setHours(0, 0, 0, 0);
            let dueInDays = null; let isOverdue = false;
            if (dueDate) {
              const diffTime = dueDate.getTime() - today.getTime();
              dueInDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
              isOverdue = dueInDays < 0;
            }

            // Determine amount and currency - prioritize specific, then original, then default
            let amount = 0;
            const currency = entry.original_currency || entry.currency || "TRY"; // More robust currency fallback
            const isOfficial = entry.is_official === true;
            const lcCurrency = currency.toLowerCase();
            const officialKey = `amount_${lcCurrency}_official`;
            const unofficialKey = `amount_${lcCurrency}_unofficial`;

            if (isOfficial && entry[officialKey] != null) amount = entry[officialKey];
            else if (!isOfficial && entry[unofficialKey] != null) amount = entry[unofficialKey];
            else if (entry.original_amount != null) amount = entry.original_amount; // Fallback to original
            else amount = entry.amount || 0; // Final fallback to generic amount

            amount = Number(amount) || 0;

            return {
              id: entry.id,
              projectId: entry.project_id,
              date: entry.date,
              dueDate: entry.due_date,
              description: entry.description,
              amount: amount,
              currency: currency,
              personId: entry.person_id,
              personName: entry.people?.name || 'Belirsiz',
              // Outgoing for legacy given checks and all expense-based checks;
              // only received checks are incoming.
              isGivenCheck: entry.type !== 'receivedCheck',
              status: entry.status || 'pending',
              // Use optional chaining for potentially missing properties
              isProcessed: entry.is_processed ?? false,
              isPostponed: entry.is_postponed ?? false,
              dueInDays,
              isOverdue,
              checkNumber: entry.check_number ?? null,
              imageUrl: entry.image_url ?? null,
            };
          });

          return checkPayments;
        } catch (err) {
          
          return [];
        }
      },
      {
        timeoutMs: 10000,
        name: `getAllProjectCheckPayments_${projectId}`,
        fallbackValue: []
      }
    );
  },

  // Create a share code for a project
  async createShareCode(projectId: string, mode: "read" | "write", expirationHours: number = 48) {
    return safeApiCall(
      async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');
        const shareCode = uuidv4().replace(/-/g, '').substring(0, 8);
        const expiresAt = new Date(); expiresAt.setHours(expiresAt.getHours() + expirationHours);

        // IMPORTANT: Use 'as any' because 'project_shares' might be undefined in types. Regenerate types.
        const { data, error } = await supabase
          .from('project_shares' as any)
          .insert({
            share_code: shareCode,
            project_id: projectId,
            from_user_id: user.id,
            mode: mode,
            expires_at: expiresAt.toISOString(),
            status: 'pending'
          } as unknown as ProjectShare) // Use 'as unknown as Type' for safer casting
          .select()
          .single();

        if (error) { throw error; }
        // Cast after checking for error
        const insertedData = data as unknown as ProjectShare;

        await logActivity( user.id, ActivityActions.CREATE_SHARE_CODE, { project_id: projectId, share_id: insertedData.id, mode: mode });

        return { shareCode, expiresAt: expiresAt.toISOString(), mode, status: 'pending' };
      },
      { timeoutMs: 10000, name: "createShareCode", critical: true }
    );
  },

  // Accept a share code and add user to project
  async acceptShareCode(shareCode: string) {
    return safeApiCall(
      async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        // IMPORTANT: Use 'as any' for table name. Regenerate types.
        const { data: shareData, error: shareError } = await supabase
          .from('project_shares' as any)
          .select('*')
          .eq('share_code', shareCode)
          .maybeSingle();

        if (shareError) { throw new Error('Paylaşım kodu kontrol edilirken hata oluştu'); }
        // Cast after checking for error and null
        const share = shareData ? shareData as unknown as ProjectShare : null;

        if (!share) { throw new Error('Geçersiz paylaşım kodu'); }
        if (share.status === 'revoked') { throw new Error('Bu paylaşım kodu iptal edilmiş'); }
        if (share.status === 'active') { throw new Error('Bu paylaşım kodu zaten kullanılmış'); }
        if (share.status === 'expired' || (share.expires_at && new Date(share.expires_at) < new Date())) {
           if (share.status !== 'expired') {
             await supabase.from('project_shares' as any).update({ status: 'expired' }).eq('id', share.id);
           }
           throw new Error('Bu paylaşım kodunun süresi dolmuş');
        }
        if (share.status !== 'pending') { throw new Error('Paylaşım kodu beklemede değil'); }

        // Fetch project data
        const { data: projectData, error: projectError } = await supabase
          .from('projects').select('id, name, user_id').eq('id', share.project_id).single();
        if (projectError) { throw new Error('Proje bilgileri alınamadı'); }

        // Check slots (fetch user data)
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('plan, projects_locked, expires_at')
          .eq('id', user.id)
          .single();
        if (userError) { throw new Error('Proje kotası kontrol edilemedi'); }
        
        // Check if user's plan has expired or projects are locked
        const now = new Date();
        const expiresAt = userData?.expires_at ? new Date(userData.expires_at) : null;
        const isExpired = expiresAt ? now > expiresAt : false;
        
        if (isExpired || userData?.projects_locked) {
          throw new Error('Proje kotanız dolu. Aboneliğinizi yükseltin.');
        }

        // --- Transaction-like sequence ---
        const role = share.mode === 'write' ? 'editor' : 'viewer';
        // 1. Add link
        const { error: linkError } = await supabase.from('project_user_link').insert({
          project_id: share.project_id, user_id: user.id, role: role, linked_via: 'share'
        });
        if (linkError) { throw new Error('Projeye eklenirken hata oluştu'); }

        // 2. Update share status
        const { error: updateError } = await supabase.from('project_shares' as any).update({
          to_user_id: user.id, used_at: new Date().toISOString(), status: 'active'
        }).eq('id', share.id);
        if (updateError) {
          // Rollback link
          await supabase.from('project_user_link').delete().eq('project_id', share.project_id).eq('user_id', user.id);
          throw new Error('Paylaşım kodu güncellenirken hata oluştu');
        }

        // 3. Mark that the user has used a project slot
        const { error: slotUpdateError } = await supabase
          .from('users')
          .update({ 
            // If they have many projects, we might want to track in a separate table
            // For now, just ensure projects_locked is false
            projects_locked: false 
          })
          .eq('id', user.id);
          
        if (slotUpdateError) {
          
        }

        await logActivity( user.id, ActivityActions.ACCEPT_SHARE_CODE, { project_id: share.project_id, share_id: share.id, from_user_id: share.from_user_id });

        return { success: true, projectId: share.project_id, projectName: projectData?.name || 'Paylaşılan Proje', role: role };
      },
      { timeoutMs: 15000, name: "acceptShareCode", critical: true }
    );
  },

  // Revoke a share code
  async revokeShareCode(shareId: string) {
    return safeApiCall(
      async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        // IMPORTANT: Use 'as any' for table name. Regenerate types.
        const { data: shareData, error: shareError } = await supabase
          .from('project_shares' as any)
          .select('*')
          .eq('id', shareId)
          .single();

        if (shareError || !shareData) { throw new Error('Paylaşım bulunamadı'); }
        // Cast after checking for error
        const share = shareData as unknown as ProjectShare;

        if (share.from_user_id !== user.id) { throw new Error('Bu kodu iptal etme yetkiniz yok'); }

        // --- Transaction-like sequence ---
        // 1. Update share status
        const { error: updateError } = await supabase.from('project_shares' as any).update({
          status: 'revoked', revoked_at: new Date().toISOString()
        }).eq('id', shareId);
        if (updateError) { throw new Error('Paylaşım iptal edilirken hata oluştu'); }

        // 2. No need to restore slots if we're not tracking with trial_days_remaining
        // Just updating the project access should be sufficient

        // 3. Add log entry
        // IMPORTANT: Use 'as any' for table name. Regenerate types.
        await supabase.from('project_share_logs' as any).insert({
          share_id: shareId, action: 'revoked', performed_by: user.id,
          note: `Share code revoked for project ${share.project_id}`
        } as unknown as ProjectShareLog); // Use placeholder type

        await logActivity( user.id, ActivityActions.REVOKE_SHARE_CODE, { project_id: share.project_id, share_id: shareId, to_user_id: share.to_user_id });

        return { success: true, message: 'Paylaşım kodu başarıyla iptal edildi' };
      },
      { timeoutMs: 10000, name: "revokeShareCode", critical: true }
    );
  },

  // Get all active share codes for a project
  async getProjectShares(projectId: string) {
    return safeApiCall(
      async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        // IMPORTANT: Use 'as any' for table name. Regenerate types.
        const { data, error } = await supabase
          .from('project_shares' as any)
          .select('*') // Select all columns defined in placeholder type
          .eq('project_id', projectId)
          .eq('from_user_id', user.id);

        if (error) { throw error; }
        // Cast after checking for error
        const shares = data ? data as unknown as ProjectShare[] : [];

        // Fetch user data for shares used by someone
        const sharesWithUserData = await Promise.all(shares.map(async (share) => {
          if (share.to_user_id) {
            const { data: userData } = await supabase
              .from('users').select('id, email, name').eq('id', share.to_user_id).single();
            return { ...share, to_user: userData }; // Combine share with user data
          }
          return { ...share, to_user: null };
        }));

        return sharesWithUserData;
      },
      { timeoutMs: 10000, name: "getProjectShares", critical: false }
    );
  },

  // Get projects shared with me
  async getSharedWithMeProjects() {
    return safeApiCall(
      async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        // Get links for projects shared with this user
        const { data: links, error: linkError } = await (supabase
          .from('view_project_members') as any) // Type assertion to bypass type errors
          .select('project_id, role, linked_via, created_at')
          .eq('user_id', user.id)
          // .eq('linked_via', 'share'); // Filter only shares if needed, or remove for all access

        if (linkError) { throw linkError; }
        if (!links) return [];

        const projectIds = links.map(link => link.project_id);
        if (projectIds.length === 0) return [];

        // Fetch project details for these IDs
        const { data: projects, error: projectError } = await supabase
          .from('projects')
          .select('*') // Select necessary fields
          .in('id', projectIds);

        if (projectError) { throw projectError; }
        if (!projects) return [];

        // Fetch owner details for these projects
        const ownerIds = [...new Set(projects.map(p => p.user_id).filter(Boolean))];
        let ownersMap: { [key: string]: { id: string, name?: string | null, email: string } } = {};
        if (ownerIds.length > 0) {
           const { data: ownersData, error: ownerError } = await supabase
               .from('users').select('id, name, email').in('id', ownerIds);
           if (!ownerError && ownersData) {
               ownersMap = ownersData.reduce((acc, owner) => {
                   acc[owner.id] = owner;
                   return acc;
               }, {});
           }
        }

        // Combine data
        const sharedProjects = projects.map(project => {
          const link = links.find(l => l.project_id === project.id);
          const owner = project.user_id ? ownersMap[project.user_id] : null;
          return {
            ...project,
            role: link?.role || 'unknown',
            linked_via: link?.linked_via || 'unknown',
            owner: owner // Add owner info
          };
        });

        return sharedProjects;
      },
      { timeoutMs: 10000, name: "getSharedWithMeProjects", critical: false }
    );
  },

  // Get members for a specific project (Rewritten)
  async getProjectMembers(projectId: string) {
    
    return safeApiCall(
      async () => {
        

        // 1. Fetch project owner info
        const { data: projectData, error: projectError } = await supabase
          .from('projects')
          .select('user_id') // Only need the owner's ID initially
          .eq('id', projectId)
          .single();

        if (projectError) {
          
          throw new Error("Proje sahibi bilgisi alınamadı.");
        }
        const ownerUserId = projectData.user_id;

        // 2. Fetch links - Using view_project_members instead of project_user_link for reading
        const { data: linksData, error: linkError } = await (supabase
          .from('view_project_members') as any) // Type assertion to bypass type errors
          .select('user_id, role, linked_via, created_at') // Include created_at
          .eq('project_id', projectId);

        if (linkError) {
          
          throw new Error("Proje üye bağlantıları alınamadı.");
        }

        // Check if linksData is an array before proceeding
        // IMPORTANT: Use 'as any[]' to bypass incorrect type inference. Regenerate types!
        const links = Array.isArray(linksData) ? (linksData as any[]) : [];

        // 3. Get all unique user IDs
        const userIds = [ownerUserId, ...links.map(link => link.user_id)]; // Access user_id safely now
        const uniqueUserIds = [...new Set(userIds)];

        // 4. Fetch user details for all involved users
        let usersMap: { [key: string]: { id: string, name?: string | null, email: string } } = {};
        if (uniqueUserIds.length > 0) {
            const { data: usersData, error: usersError } = await supabase
                .from('users')
                .select('id, name, email')
                .in('id', uniqueUserIds);

            if (usersError) {
                
                throw new Error("Üye detayları alınamadı.");
            }
            if (usersData) {
                usersMap = usersData.reduce((acc, user) => {
                    acc[user.id] = user;
                    return acc;
                }, {});
            }
        }

        // 5. Construct the final members list
        const ownerMember = usersMap[ownerUserId] ? {
            id: `owner-${ownerUserId}`,
            role: 'owner',
            linked_via: null, // Use linked_via, set to null for owner
            created_at: null, // Set created_at to null for owner
            user: usersMap[ownerUserId]
        } : null;

        const otherMembers = links
            .filter(link => link.user_id !== ownerUserId) // Safe access
            .map(link => ({
                id: link.user_id,       // Safe access
                role: link.role,       // Safe access
                linked_via: link.linked_via, // Use linked_via
                created_at: link.created_at, // Include created_at field
                user: usersMap[link.user_id] || { id: link.user_id, email: 'Unknown Email' } // Safe access
            }));

        const allMembers = ownerMember ? [ownerMember, ...otherMembers] : otherMembers;

        
        return allMembers;
      },
      {
        timeoutMs: 10000,
        name: `getProjectMembers_${projectId}`,
        queueIfResuming: true,
        fallbackValue: []
      }
    );
  },

  // Add a member to a project
  async addProjectMember(projectId: string, userIdToAdd: string, role: 'viewer' | 'editor') {
    
    return safeApiCall(
      async () => {
        

        // Make sure both IDs are valid UUIDs
        if (!projectId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i) || 
            !userIdToAdd.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
          
          throw new Error("Geçersiz kullanıcı veya proje kimliği.");
        }

        // Test session - this is critical for authentication
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) {
            
            throw new Error('Oturum bulunamadı. Lütfen tekrar giriş yapın.');
          }
          const currentUserId = session.user.id;
          
        } catch (sessionError) {
          
          throw new Error('Oturum bilgisi alınamadı. Lütfen internet bağlantınızı kontrol edin.');
        }

        try {
          // Check if the project exists
          const { data: projectData, error: projectError } = await supabase
            .from('projects').select('user_id').eq('id', projectId).single();
          
          if (projectError) { 
            
            throw new Error("Proje bilgileri alınamadı. Proje mevcut olmayabilir."); 
          }
          
          if (!projectData) {
            
            throw new Error("Proje bulunamadı."); 
          }
          
          if (projectData?.user_id === userIdToAdd) { 
            
            throw new Error("Proje sahibi zaten tam erişime sahip."); 
          }

          // Check if there's already a link
          const { data: existingLink, error: linkCheckError } = await supabase
            .from('project_user_link')
            .select('project_id, user_id')
            .eq('project_id', projectId)
            .eq('user_id', userIdToAdd)
            .maybeSingle();
          
          if (linkCheckError) { 
            
            throw new Error("Kullanıcı durumu kontrol edilirken bir hata oluştu."); 
          }
          
          if (existingLink) { 
            
            throw new Error("Bu kullanıcı zaten projeye eklenmiş."); 
          }

          // Check if user exists
          const { data: userData, error: userCheckError } = await supabase
            .from('users').select('id').eq('id', userIdToAdd).maybeSingle();
            
          if (userCheckError) {
            
            throw new Error("Kullanıcı bilgileri kontrol edilirken bir hata oluştu.");
          }
          
          if (!userData) {
            
            throw new Error("Eklenmek istenen kullanıcı bulunamadı.");
          }

          // Insert link with verbose error handling
          const insertResult = await supabase
            .from('project_user_link')
            .insert({
              project_id: projectId,
              user_id: userIdToAdd,
              role: role,
              linked_via: 'share'
            });
            
          const insertError = insertResult.error;
          
          if (insertError) {
            
            
            if (insertError.code === '23505') { // PostgreSQL unique violation
              throw new Error("Bu kullanıcı zaten projeye eklenmiş.");
            }
            
            if (insertError.code?.includes('permission') || insertError.message?.includes('permission')) {
              throw new Error("Yetki hatası: Bu işlemi gerçekleştirmek için gerekli izniniz yok.");
            }
            
            throw new Error(`Kullanıcı projeye eklenemedi: ${insertError.message || 'Veritabanı hatası'}`);
          }

          

          // Get session again for logging activity
          const { data: { session } } = await supabase.auth.getSession();
          if (session && ActivityActions && typeof ActivityActions.ADD_PROJECT_MEMBER === 'string') {
            try {
              await logActivity(session.user.id, ActivityActions.ADD_PROJECT_MEMBER,
                { project_id: projectId, added_user_id: userIdToAdd, role: role }
              );
            } catch (logError) {
              
            }
          }

          return { success: true, message: "Kullanıcı başarıyla eklendi." };
        } catch (err) {
          
          throw err; // Re-throw to be caught by safeApiCall
        }
      },
      { timeoutMs: 25000, name: `addProjectMember_${projectId}_${userIdToAdd}`, critical: true }
    );
  },

  // Remove a member from a project
  async removeProjectMember(projectId: string, userIdToRemove: string) {
    
    return safeApiCall(
      async () => {
        

        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('User not authenticated');
        const currentUserId = session.user.id;

        // Get project info to check ownership
        const { data: projectData, error: projectError } = await supabase
          .from('projects')
          .select('user_id')
          .eq('id', projectId)
          .single();
          
        if (projectError) { 
          
          throw new Error("Proje bilgileri alınamadı."); 
        }

        if (projectData?.user_id === userIdToRemove) {
            
            throw new Error("Proje sahibi kaldırılamaz.");
        }

        // Check if the user is actually a member before trying to remove
        const { data: membership, error: membershipError } = await supabase
          .from('project_user_link')
          .select('project_id, user_id, role')
          .eq('project_id', projectId)
          .eq('user_id', userIdToRemove)
          .maybeSingle();
          
        if (membershipError) {
          
          throw new Error("Üye durumu kontrol edilirken bir hata oluştu.");
        }
        
        if (!membership) {
          
          return { success: true, message: "Kullanıcı zaten projede değil." };
        }

        // Delete the link
        const { error: deleteError } = await supabase
          .from('project_user_link')
          .delete()
          .eq('project_id', projectId)
          .eq('user_id', userIdToRemove);

        if (deleteError) {
          
          // Check if the error is "No rows found" (code PGRST116), which isn't a failure if we tried to delete non-existent link
          if (deleteError.code !== 'PGRST116') {
              throw new Error("Kullanıcı projeden kaldırılamadı. Veritabanı hatası.");
          } else {
              
              // Return success even if the user wasn't found, as the desired state (user removed) is achieved.
          }
        }

         

        // Log activity safely checking if action exists in enum
        if (ActivityActions && typeof ActivityActions.REMOVE_PROJECT_MEMBER === 'string') {
            await logActivity( currentUserId, ActivityActions.REMOVE_PROJECT_MEMBER,
               { project_id: projectId, removed_user_id: userIdToRemove }
            );
        } else {
            
        }

        return { success: true, message: "Kullanıcı başarıyla kaldırıldı." };
      },
      { timeoutMs: 15000, name: `removeProjectMember_${projectId}_${userIdToRemove}`, critical: true }
    );
  },

  // Get current user's role for a specific project
  async getUserRoleForProject(projectId: string) {
    return safeApiCall(
      async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        // First check if user is the project owner
        const { data: projectData, error: projectError } = await supabase
          .from('projects')
          .select('user_id')
          .eq('id', projectId)
          .single();

        if (projectError) {
          
          throw new Error('Proje bilgisi alınamadı');
        }

        // If user is the project owner, return owner role
        if (projectData.user_id === user.id) {
          return {
            role: 'owner',
            hasAccess: true,
            canEdit: true,
            canComplete: true,
            canUploadFiles: true
          };
        }

        // Check if user has a role through project_user_link
        const { data: linkData, error: linkError } = await supabase
          .from('project_user_link')
          .select('role')
          .eq('project_id', projectId)
          .eq('user_id', user.id)
          .maybeSingle();

        if (linkError) {
          
          throw new Error('Kullanıcı proje erişimi kontrol edilemedi');
        }

        if (!linkData) {
          return {
            role: null,
            hasAccess: false,
            canEdit: false,
            canComplete: false,
            canUploadFiles: false
          };
        }

        const role = linkData.role;
        return {
          role,
          hasAccess: true,
          canEdit: role === 'editor' || role === 'owner',
          canComplete: role === 'owner', // Only owners can complete projects
          canUploadFiles: role === 'editor' || role === 'owner' // Editors and owners can upload/delete files
        };
      },
      { timeoutMs: 10000, name: `getUserRoleForProject_${projectId}`, critical: true }
    );
  },

  // Leave a project (for viewers/editors)
  async leaveProject(projectId: string) {
    return safeApiCall(
      async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        // Check if user is the project owner (owners can't leave their own projects)
        const { data: projectData, error: projectError } = await supabase
          .from('projects')
          .select('user_id')
          .eq('id', projectId)
          .single();

        if (projectError) {
          
          throw new Error('Proje bilgisi alınamadı');
        }

        if (projectData.user_id === user.id) {
          throw new Error('Proje sahipleri kendi projelerinden ayrılamaz');
        }

        // Remove the user from the project
        const { error: deleteError } = await supabase
          .from('project_user_link')
          .delete()
          .eq('project_id', projectId)
          .eq('user_id', user.id);

        if (deleteError) {
          
          throw new Error('Projeden ayrılırken bir hata oluştu');
        }

        // Log activity
        if (ActivityActions && typeof ActivityActions.LEAVE_PROJECT === 'string') {
          try {
            await logActivity(user.id, ActivityActions.LEAVE_PROJECT, {
              project_id: projectId
            });
          } catch (logError) {
            
          }
        }

        return { success: true, message: 'Projeden başarıyla ayrıldınız' };
      },
      { timeoutMs: 15000, name: `leaveProject_${projectId}`, critical: true }
    );
  },
};